// apps/hr-frontend/src/components/EmployeeLimitBanner.tsx
import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { UserContext } from '../context/UserContext';
import { getEmployeeLimitInfo } from '../services/api';
import { isSubscriptionEnabled } from '../utils/environment';

interface EmployeeLimitInfo {
  isWithinLimit: boolean;
  currentCount: number;
  limit: number;
  planName: string;
  exceeded: number;
}

/** 還能新增員工：無上限（0）或目前數量未達上限（照 CRM canCreateCustomer；後端 assertEmployeeQuota 同規則） */
export const canCreateEmployee = (info: { currentCount: number; limit: number }) => info.limit === 0 || info.currentCount < info.limit;

interface EmployeeLimitBannerProps {
  /** 還能新增員工（等於上限就不能再新增員工） */
  onCanCreateEmployeeChange?: (canCreate: boolean) => void;
  showAlways?: boolean; // 管理員頁面始終顯示
  refreshTrigger?: number; // 當這個值變化時，重新獲取員工限制資訊
}

// Docker 版本不顯示員工限制橫幅，但仍要通知父組件可以新增員工（沒有限制）
// 拆成獨立元件：Hooks 不能放在 if 裡（React Rules of Hooks）
const UnlimitedNotifier: React.FC<Pick<EmployeeLimitBannerProps, 'onCanCreateEmployeeChange'>> = ({ onCanCreateEmployeeChange }) => {
  useEffect(() => {
    onCanCreateEmployeeChange?.(true);
  }, [onCanCreateEmployeeChange]);
  return null;
};

const EmployeeLimitBanner: React.FC<EmployeeLimitBannerProps> = (props) =>
  isSubscriptionEnabled() ? <SubscriptionLimitBanner {...props} /> : <UnlimitedNotifier onCanCreateEmployeeChange={props.onCanCreateEmployeeChange} />;

const SubscriptionLimitBanner: React.FC<EmployeeLimitBannerProps> = ({ onCanCreateEmployeeChange, showAlways = false, refreshTrigger }) => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const { user } = userContext!;

  // 嘗試從 localStorage 恢復之前的狀態，避免初次載入時的跳動
  const [limitInfo, setLimitInfo] = useState<EmployeeLimitInfo | null>(() => {
    if (!user?.organizationId) return null;
    try {
      const cached = localStorage.getItem(`employee-limit-${user.organizationId}`);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [isInitialLoading, setIsInitialLoading] = useState(!limitInfo); // 如果有快取，不需要初次載入狀態

  const fetchLimitInfo = useCallback(async (isInitial = false) => {
    if (!user?.id) return;
    
    try {
      if (isInitial) {
        setIsInitialLoading(true);
      }
      
      const response = await getEmployeeLimitInfo();
      const data = response.data;
      setLimitInfo(data);

      // 將數據快取到 localStorage，下次載入時可以立即顯示
      try {
        localStorage.setItem(`employee-limit-${user.organizationId}`, JSON.stringify(data));
      } catch (error) {
        console.warn('Failed to cache employee limit info:', error);
      }
      
      // 通知父組件是否可以新增
      onCanCreateEmployeeChange?.(canCreateEmployee(data));
    } catch (error) {
      console.error('Failed to fetch employee limit info:', error);
    } finally {
      if (isInitial) {
        setIsInitialLoading(false);
      }
    }
  }, [user?.id, onCanCreateEmployeeChange]);

  useEffect(() => {
    if (user?.id) {
      fetchLimitInfo(true);
    }
  }, [user?.id, fetchLimitInfo]);

  // 當 refreshTrigger 變化時，重新獲取員工限制資訊
  useEffect(() => {
    if (user?.id && refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchLimitInfo(false);
    }
  }, [refreshTrigger, user?.id, fetchLimitInfo]);

  // 如果是初次載入且沒有快取，不顯示組件避免閃爍
  if (isInitialLoading && !limitInfo) {
    return null;
  }

  // 移除載入狀態的顯示，避免組件消失造成畫面跳動
  // 刷新時保持顯示舊數據，在背景更新

  if (!limitInfo) return null;

  // 一般頁面只在超過限制時顯示，管理員頁面始終顯示
  if (!showAlways && limitInfo.isWithinLimit) {
    return null;
  }

  const getProgressPercentage = () => {
    if (limitInfo.limit === 0) return 0; // 無限制
    return Math.min((limitInfo.currentCount / limitInfo.limit) * 100, 100);
  };


  return (
    // 用量資訊：平常是中性細框；只有超過上限（需要使用者處理）才用紅色（§3-5），不放 emoji
    <div className={`rounded-lg px-4 py-3 mb-6 border ${
      limitInfo.isWithinLimit
        ? 'bg-surface border-line text-fg'
        : 'border-red-300 dark:border-red-800 text-red-700 dark:text-red-400'
    }`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center">
          <div>
            <h3 className="text-sm">
              {limitInfo.limit === 0 ? (
                <Trans
                  key="unlimited"
                  i18nKey="resourceLimit:unlimitedUsage"
                  values={{
                    planName: t(`resourceLimit:plans.${limitInfo.planName}`, limitInfo.planName),
                    currentCount: limitInfo.currentCount,
                    entityName: t('translation:entityNames.employee')
                  }}
                  components={{
                    bold: <strong key="bold-unlimited" />
                  }}
                />
              ) : (
                <span key="limited">
                  <Trans
                    key="limited-usage"
                    i18nKey="resourceLimit:limitedUsage"
                    values={{
                      planName: t(`resourceLimit:plans.${limitInfo.planName}`, limitInfo.planName),
                      currentCount: limitInfo.currentCount,
                      limit: limitInfo.limit,
                      entityName: t('translation:entityNames.employee')
                    }}
                    components={{
                      bold: <strong key="bold-limited" />
                    }}
                  />
                  {!limitInfo.isWithinLimit && (
                    <span key="exceeded" className="ml-2 font-medium">
                      {t('resourceLimit:exceeded', { exceeded: limitInfo.exceeded })}
                    </span>
                  )}
                </span>
              )}
            </h3>
            {!limitInfo.isWithinLimit && (
              <p className="text-[13px] mt-0.5">
                {t('resourceLimit:archiveOrUpgrade', { exceeded: limitInfo.exceeded, entityName: t('translation:entityNames.employee') })}
              </p>
            )}
          </div>
        </div>

        {limitInfo.limit > 0 && (
          <div className="flex items-center gap-3 shrink-0">
            <div className="w-32 bg-slate-100 dark:bg-slate-800 rounded-full h-1.5">
              <div 
                className={`h-1.5 rounded-full ${
                  limitInfo.isWithinLimit ? 'bg-accent' : 'bg-red-500'
                }`}
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
            <span className="text-[13px] tabular-nums text-fg-secondary">
              {Math.round(getProgressPercentage())}%
            </span>
          </div>
        )}
      </div>

    </div>
  );
};

export default EmployeeLimitBanner;